import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePickerModule } from 'primeng/datepicker';
import { DrawerModule } from 'primeng/drawer';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';

import { Equipamento } from '@core/models/models';
import { FeedbackService } from '@core/services/feedback.service';
import { environment } from '@environments/environment';

export interface RegistoAbastecimento {
  id: string | number;
  equipamentoId: number;
  data: string;
  litros: number;
  custo: number;
  quilometragem: number;
  postoCombustivel: string;
  motorista: string;
  tipoCombustivel?: string;
  comprovativoNome?: string;
  observacoes?: string;
  criadoEm?: string;
}

@Component({
  selector: 'app-equipamento-combustivel-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePickerModule,
    DrawerModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TextareaModule,
    TooltipModule,
  ],
  templateUrl: './equipamento-combustivel-tab.component.html',
  styleUrl: './equipamento-combustivel-tab.component.scss',
})
export class EquipamentoCombustivelTabComponent implements OnInit, OnChanges {
  private readonly feedback = inject(FeedbackService);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  @Input() equipamento: Equipamento | null = null;
  @Output() combustivelChange = new EventEmitter<{
    registo: RegistoAbastecimento;
    novoKm: number;
  }>();

  registos: RegistoAbastecimento[] = [];
  loading = false;
  drawerVisible = false;
  saving = false;

  readonly postosSugeridos = [
    { label: 'TotalEnergies Benfica', value: 'Total Benfica' },
    { label: 'TotalEnergies Luanda Sul', value: 'Total Luanda Sul' },
    { label: 'Sonangol Maianga', value: 'Sonangol Maianga' },
    { label: 'Sonangol Viana', value: 'Sonangol Viana' },
    { label: 'Pumangol Talatona', value: 'Pumangol Talatona' },
    { label: 'BP Viana', value: 'BP Viana' },
    { label: 'Outro Posto', value: 'Outro' },
  ];

  readonly tiposCombustivel = [
    { label: 'Gasóleo (Diesel)', value: 'Gasóleo' },
    { label: 'Gasolina', value: 'Gasolina' },
  ];

  form = {
    data: new Date() as Date | null,
    litros: null as number | null,
    custo: null as number | null,
    quilometragem: null as number | null,
    postoCombustivel: 'Total Benfica',
    postoCustom: '',
    motorista: '',
    tipoCombustivel: 'Gasóleo',
    observacoes: '',
  };

  selectedFile: File | null = null;

  ngOnInit(): void {
    this.carregarRegistos();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['equipamento']) {
      this.carregarRegistos();
    }
  }

  private getStorageKey(): string {
    return `sp_combustivel_equip_${this.equipamento?.id ?? 0}`;
  }

  get mesAtualNome(): string {
    const meses = [
      'Janeiro',
      'Fevereiro',
      'Março',
      'Abril',
      'Maio',
      'Junho',
      'Julho',
      'Agosto',
      'Setembro',
      'Outubro',
      'Novembro',
      'Dezembro',
    ];
    return meses[new Date().getMonth()];
  }

  // ── Filtro de registos do mês corrente ───────────────────────────
  get registosMesAtual(): RegistoAbastecimento[] {
    const hoje = new Date();
    const anoAtual = hoje.getFullYear();
    const mesAtual = hoje.getMonth();

    const doMes = this.registos.filter((r) => {
      const d = new Date(r.data);
      return d.getFullYear() === anoAtual && d.getMonth() === mesAtual;
    });

    // Se não houver registos no mês atual exato (ex: base mockada de abril), usa todos os registos recentes
    return doMes.length ? doMes : this.registos;
  }

  // ── KPI 1: Total Gasto no Mês (AOA) ──────────────────────────────
  get totalGastoMes(): number {
    return this.registosMesAtual.reduce(
      (sum, r) => sum + Number(r.custo || 0),
      0,
    );
  }

  // ── KPI 2: Litros Abastecidos no Mês ─────────────────────────────
  get totalLitrosMes(): number {
    return this.registosMesAtual.reduce(
      (sum, r) => sum + Number(r.litros || 0),
      0,
    );
  }

  // ── KPI 3: Km Percorridos no Mês ─────────────────────────────────
  get kmPercorridosMes(): number {
    if (this.registosMesAtual.length < 2) {
      if (this.registosMesAtual.length === 1 && this.totalLitrosMes > 0) {
        return Math.round((this.totalLitrosMes / 9.2) * 100);
      }
      return 0;
    }

    const kms = this.registosMesAtual
      .map((r) => Number(r.quilometragem || 0))
      .filter((k) => k > 0);

    if (kms.length < 2) return 0;

    const maxKm = Math.max(...kms);
    const minKm = Math.min(...kms);
    const diff = maxKm - minKm;

    return diff > 0 ? diff : 0;
  }

  // ── KPI 4: Consumo Médio (L/100km) ───────────────────────────────
  get consumoMedio(): number {
    const km = this.kmPercorridosMes;
    const litros = this.totalLitrosMes;

    if (km > 0 && litros > 0) {
      const val = (litros / km) * 100;
      return Number(val.toFixed(1));
    }
    return 9.2; // Consumo de referência padrão
  }

  carregarRegistos(): void {
    if (!this.equipamento?.id) return;

    const stored = localStorage.getItem(this.getStorageKey());
    if (stored) {
      try {
        this.registos = JSON.parse(stored);
        return;
      } catch {
        this.registos = [];
      }
    }

    // Gerar registos iniciais consistentes com o mockup
    const kmAtualNum =
      Number(this.getPropriedadeValor('QUILOMETRAGEM') || this.getPropriedadeValor('KM_ATUAL')) ||
      45230;

    const motoristaPadrao =
      this.getPropriedadeValor('MOTORISTA') ||
      this.getPropriedadeValor('MOTORISTA_ATRIBUIDO') ||
      'João Manuel';

    const baseAno = new Date().getFullYear();
    const baseMes = String(new Date().getMonth() + 1).padStart(2, '0');

    this.registos = [
      {
        id: '1',
        equipamentoId: this.equipamento.id,
        data: `${baseAno}-${baseMes}-12`,
        litros: 80,
        custo: 32000,
        quilometragem: kmAtualNum,
        postoCombustivel: 'Total Benfica',
        motorista: motoristaPadrao,
        tipoCombustivel: 'Gasóleo',
      },
      {
        id: '2',
        equipamentoId: this.equipamento.id,
        data: `${baseAno}-${baseMes}-08`,
        litros: 75,
        custo: 30000,
        quilometragem: kmAtualNum - 420,
        postoCombustivel: 'Total Luanda Sul',
        motorista: motoristaPadrao,
        tipoCombustivel: 'Gasóleo',
      },
      {
        id: '3',
        equipamentoId: this.equipamento.id,
        data: `${baseAno}-${baseMes}-04`,
        litros: 82,
        custo: 32800,
        quilometragem: kmAtualNum - 810,
        postoCombustivel: 'Total Benfica',
        motorista: motoristaPadrao,
        tipoCombustivel: 'Gasóleo',
      },
      {
        id: '4',
        equipamentoId: this.equipamento.id,
        data: `${baseAno}-${String(Math.max(1, new Date().getMonth())).padStart(2, '0')}-28`,
        litros: 75,
        custo: 29200,
        quilometragem: kmAtualNum - 1250,
        postoCombustivel: 'BP Viana',
        motorista: 'Pedro Silva',
        tipoCombustivel: 'Gasóleo',
      },
    ];

    localStorage.setItem(this.getStorageKey(), JSON.stringify(this.registos));
  }

  openCreate(): void {
    const ultimoRegisto = this.registos[0];
    const kmAtual =
      Number(this.getPropriedadeValor('QUILOMETRAGEM') || this.getPropriedadeValor('KM_ATUAL')) ||
      (ultimoRegisto ? ultimoRegisto.quilometragem : 45000);

    const motoristaPadrao =
      this.getPropriedadeValor('MOTORISTA') ||
      this.getPropriedadeValor('MOTORISTA_ATRIBUIDO') ||
      (ultimoRegisto ? ultimoRegisto.motorista : 'João Manuel');

    this.form = {
      data: new Date(),
      litros: 75,
      custo: 30000,
      quilometragem: kmAtual + 350,
      postoCombustivel: 'Total Benfica',
      postoCustom: '',
      motorista: motoristaPadrao,
      tipoCombustivel: 'Gasóleo',
      observacoes: '',
    };

    this.selectedFile = null;
    this.drawerVisible = true;
  }

  closeDrawer(): void {
    if (this.saving) return;
    this.drawerVisible = false;
    this.selectedFile = null;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.selectedFile = input.files[0];
    }
  }

  removeFile(): void {
    this.selectedFile = null;
  }

  calcularCustoSugerido(): void {
    if (this.form.litros && !this.form.custo) {
      const precoLitro = this.form.tipoCombustivel === 'Gasolina' ? 300 : 400;
      this.form.custo = Math.round(this.form.litros * precoLitro);
    }
  }

  salvarRegisto(): void {
    if (!this.equipamento?.id) return;

    if (!this.form.data) {
      this.feedback.showWarning('Informe a data do abastecimento.');
      return;
    }

    if (!this.form.litros || this.form.litros <= 0) {
      this.feedback.showWarning('Informe a quantidade de litros abastecidos.');
      return;
    }

    if (!this.form.custo || this.form.custo <= 0) {
      this.feedback.showWarning('Informe o custo total do abastecimento.');
      return;
    }

    if (!this.form.quilometragem || this.form.quilometragem <= 0) {
      this.feedback.showWarning('Informe a quilometragem no momento do abastecimento.');
      return;
    }

    this.saving = true;

    const postoFinal =
      this.form.postoCombustivel === 'Outro' && this.form.postoCustom
        ? this.form.postoCustom.trim()
        : this.form.postoCombustivel;

    const dataStr = this.formatDateISO(this.form.data);

    const novoRegisto: RegistoAbastecimento = {
      id: Date.now(),
      equipamentoId: this.equipamento.id,
      data: dataStr,
      litros: Number(this.form.litros),
      custo: Number(this.form.custo),
      quilometragem: Number(this.form.quilometragem),
      postoCombustivel: postoFinal,
      motorista: this.form.motorista.trim() || 'Não especificado',
      tipoCombustivel: this.form.tipoCombustivel,
      observacoes: this.form.observacoes.trim(),
      comprovativoNome: this.selectedFile?.name,
      criadoEm: new Date().toISOString(),
    };

    // Inserir no topo e salvar
    this.registos.unshift(novoRegisto);
    this.registos.sort(
      (a, b) => new Date(b.data).getTime() - new Date(a.data).getTime(),
    );

    localStorage.setItem(this.getStorageKey(), JSON.stringify(this.registos));

    // Upload do comprovativo se fornecido
    if (this.selectedFile) {
      const fd = new FormData();
      fd.append(
        'Designacao',
        `Fatura Abastecimento (${novoRegisto.litros}L) - ${this.formatDate(novoRegisto.data)}`,
      );
      fd.append('Ficheiro', this.selectedFile, this.selectedFile.name);
      this.http
        .put<any>(
          `${environment.apiurl}/api/Equipamentos/${this.equipamento.id}/AdicionarDocumento`,
          fd,
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          error: (e: any) =>
            console.warn('Erro ao anexar comprovativo de combustível:', e),
        });
    }

    this.saving = false;
    this.drawerVisible = false;

    // Emite evento para atualizar o Km Atual e recarregar dados no pai
    this.combustivelChange.emit({
      registo: novoRegisto,
      novoKm: novoRegisto.quilometragem,
    });

    this.feedback.showSuccess(
      `Abastecimento de ${novoRegisto.litros} L registado com sucesso! Km atualizado para ${this.formatKm(novoRegisto.quilometragem)}.`,
    );
  }

  private formatDateISO(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDate(value?: string | Date | null): string {
    if (!value) return '—';
    try {
      const [y, m, d] = String(value).slice(0, 10).split('-');
      if (y && m && d) return `${d}/${m}/${y}`;
      const dt = value instanceof Date ? value : new Date(value);
      return dt.toLocaleDateString('pt-AO');
    } catch {
      return String(value);
    }
  }

  formatKz(value?: number | null): string {
    if (value === null || value === undefined) return '0';
    return new Intl.NumberFormat('pt-AO', {
      maximumFractionDigits: 0,
    }).format(value);
  }

  formatKm(value?: number | null): string {
    if (value === null || value === undefined) return '0';
    return new Intl.NumberFormat('pt-AO', {
      maximumFractionDigits: 0,
    }).format(value);
  }

  private getPropriedadeValor(nome: string): string {
    return (
      this.equipamento?.propriedades?.find(
        (p) => p.nome?.toUpperCase() === nome.toUpperCase(),
      )?.valor ?? ''
    );
  }
}
